import { Type } from "@sinclair/typebox";
/**
 * TypeBox schemas for per-category `Prospect.attributes` payloads on the 4
 * new collector categories. The mobile schema-driven form emits attribute
 * bags shaped by these schemas. The backend validates each payload with
 * `Value.Check` before persisting.
 *
 * Key names MUST match between mobile schema config and backend TypeBox —
 * see the tech-spec's F11/G12 rule.
 *
 * Money fields (`purchasePriceCents`, `pricePerBottleCents`,
 * `insuredAmountOverrideCents`) are integer minor units (SGD cents) per
 * project-context.md financial rule — floats are banned to avoid rounding
 * drift on trigger comparisons and premium math.
 *
 * `hasValuationReport` (Yes/No) is the SINGLE SOURCE OF TRUTH for the
 * valuation check, used by BOTH the client pop-ups and the underwriter
 * evaluator. (This supersedes the earlier F12 approach, which derived
 * valuation presence from `documents[].type === 'valuation_report'`; the
 * client immediate pop-up fires on the Item Information step, before any
 * document is uploaded, so the persisted answer is the only value available to
 * both sides — keying both off it avoids the client/server divergence.)
 */
const ArtCategoryEnum = Type.Union([
    Type.Literal("painting"),
    Type.Literal("worksOnPaper"),
    Type.Literal("sculpture"),
    Type.Literal("furniture"),
    Type.Literal("ceramics"),
    Type.Literal("metalware"),
    Type.Literal("architecturalFeatures"),
    Type.Literal("collectibles"),
    Type.Literal("other"),
]);
const StoragePlaceEnum = Type.Union([
    Type.Literal("home"),
    Type.Literal("external_storage"),
]);
const YesNo = Type.Union([Type.Literal("Yes"), Type.Literal("No")]);
const InsuredAmountMode = Type.Union([
    Type.Literal("same_as_purchase"),
    Type.Literal("other"),
]);
const IntegerCents = Type.Integer({ minimum: 0 });
/**
 * Reason literal for `attributes.underwriterReviewReason`. Emitted by the
 * backend evaluator (regions/sg/items/underwriter_review_evaluator) when a
 * trigger fires and injected into the prospect's attributes. Declaring it
 * on every category attributes schema keeps a downstream `Value.Check(...)`
 * against the persisted document valid.
 */
const UnderwriterReviewReasonEnum = Type.Optional(Type.Union([
    Type.Literal("purchase_date_over_3yrs_no_valuation"),
    Type.Literal("insured_exceeds_purchase_no_valuation"),
    Type.Literal("previous_losses_declared"),
    Type.Literal("item_value_exceeds_limit"),
    Type.Literal("customer_total_exceeds_limit"),
]));
const UnderwriterReviewRejectionReason = Type.Optional(Type.String());
export const ArtAttributesSchema = Type.Object({
    category: ArtCategoryEnum,
    categorySpecify: Type.Optional(Type.String()),
    creator: Type.String({ minLength: 1 }),
    itemTitle: Type.String({ minLength: 1 }),
    description: Type.Optional(Type.String()),
    yearOfCreation: Type.String({ minLength: 1 }),
    dimensions: Type.Optional(Type.String()),
    purchaseDate: Type.String({ minLength: 1 }),
    purchasePriceCents: IntegerCents,
    insuredAmountMode: InsuredAmountMode,
    insuredAmountOverrideCents: Type.Optional(IntegerCents),
    // Single source of truth for the valuation check (client pop-ups + the
    // underwriter evaluator both key off this answer). Supersedes the earlier
    // document-derived approach.
    hasValuationReport: YesNo,
    previousLosses: YesNo,
    insuredPersonName: Type.Optional(Type.String()),
    storagePlace: StoragePlaceEnum,
    storagePlaceSpecify: Type.Optional(Type.String()),
    securitySystems: Type.Optional(Type.Array(Type.String())),
    securitySystemsSpecify: Type.Optional(Type.String()),
    fireProtection: Type.Optional(Type.Array(Type.String())),
    underwriterReviewReason: UnderwriterReviewReasonEnum,
    underwriterReviewRejectionReason: UnderwriterReviewRejectionReason,
});
const BottleSizeWine = Type.Union([
    Type.Literal("750"),
    Type.Literal("1500"),
    Type.Literal("3000"),
    Type.Literal("other"),
]);
const OriginalPackagingWine = Type.Array(Type.Union([
    Type.Literal("box"),
    Type.Literal("wooden_case"),
    Type.Literal("certificate"),
]));
const QuantityEnum = Type.Union([
    Type.Literal("1"),
    Type.Literal("6"),
    Type.Literal("12"),
    Type.Literal("other"),
]);
export const WineAttributesSchema = Type.Object({
    producer: Type.String({ minLength: 1 }),
    nameExpression: Type.String({ minLength: 1 }),
    vintage: Type.String({ minLength: 1 }),
    countryRegion: Type.String({ minLength: 1 }),
    bottleSize: BottleSizeWine,
    bottleSizeSpecify: Type.Optional(Type.String()),
    pricePerBottleCents: IntegerCents,
    quantity: QuantityEnum,
    quantitySpecify: Type.Optional(Type.String()),
    purchaseDate: Type.String({ minLength: 1 }),
    originalPackaging: Type.Optional(OriginalPackagingWine),
    storagePlace: StoragePlaceEnum,
    storagePlaceSpecify: Type.Optional(Type.String()),
    accessControl: Type.Optional(YesNo),
    accessControlSpecify: Type.Optional(Type.String()),
    climateControl: Type.Optional(YesNo),
    climateControlSpecify: Type.Optional(Type.String()),
    underwriterReviewReason: UnderwriterReviewReasonEnum,
    underwriterReviewRejectionReason: UnderwriterReviewRejectionReason,
});
const BottleSizeWhisky = Type.Union([
    Type.Literal("700"),
    Type.Literal("750"),
    Type.Literal("other"),
]);
const OriginalPackagingWhisky = Type.Array(Type.Union([
    Type.Literal("box"),
    Type.Literal("wooden_case"),
    Type.Literal("certificate"),
    Type.Literal("stopper"),
]));
export const WhiskyAttributesSchema = Type.Object({
    distillery: Type.String({ minLength: 1 }),
    nameExpression: Type.String({ minLength: 1 }),
    ageStatement: Type.String({ minLength: 1 }),
    abvPercent: Type.Number({ minimum: 0, maximum: 100 }),
    vintage: Type.String({ minLength: 1 }),
    countryRegion: Type.String({ minLength: 1 }),
    bottleSize: BottleSizeWhisky,
    bottleSizeSpecify: Type.Optional(Type.String()),
    bottlingYear: Type.Optional(Type.String()),
    serialNumber: Type.Optional(Type.String()),
    pricePerBottleCents: IntegerCents,
    quantity: QuantityEnum,
    quantitySpecify: Type.Optional(Type.String()),
    purchaseDate: Type.String({ minLength: 1 }),
    originalPackaging: Type.Optional(OriginalPackagingWhisky),
    storagePlace: StoragePlaceEnum,
    storagePlaceSpecify: Type.Optional(Type.String()),
    accessControl: Type.Optional(YesNo),
    accessControlSpecify: Type.Optional(Type.String()),
    climateControl: Type.Optional(YesNo),
    climateControlSpecify: Type.Optional(Type.String()),
    underwriterReviewReason: UnderwriterReviewReasonEnum,
    underwriterReviewRejectionReason: UnderwriterReviewRejectionReason,
});
const GradingCompanyEnum = Type.Union([
    Type.Literal("psa"),
    Type.Literal("cgc"),
    Type.Literal("bgs"),
    Type.Literal("sgc"),
    Type.Literal("other"),
]);
export const TradingCardAttributesSchema = Type.Object({
    gameSport: Type.String({ minLength: 1 }),
    setExpansion: Type.String({ minLength: 1 }),
    cardSubject: Type.String({ minLength: 1 }),
    cardNumberInSet: Type.String({ minLength: 1 }),
    yearOfManufacture: Type.String({ minLength: 1 }),
    purchaseDate: Type.String({ minLength: 1 }),
    purchasePriceCents: IntegerCents,
    insuredAmountMode: InsuredAmountMode,
    insuredAmountOverrideCents: Type.Optional(IntegerCents),
    // Single source of truth for the valuation check (see ArtAttributesSchema).
    hasValuationReport: YesNo,
    previousLosses: YesNo,
    gradingCompany: GradingCompanyEnum,
    gradingCompanySpecify: Type.Optional(Type.String()),
    assignedGrade: Type.Number({ minimum: 1, maximum: 10 }),
    certificationNumber: Type.String({ minLength: 1 }),
    storagePlace: StoragePlaceEnum,
    storagePlaceSpecify: Type.Optional(Type.String()),
    accessControl: Type.Optional(YesNo),
    accessControlSpecify: Type.Optional(Type.String()),
    climateControl: Type.Optional(YesNo),
    climateControlSpecify: Type.Optional(Type.String()),
    underwriterReviewReason: UnderwriterReviewReasonEnum,
    underwriterReviewRejectionReason: UnderwriterReviewRejectionReason,
});
export const ATTRIBUTES_SCHEMAS = {
    art: ArtAttributesSchema,
    wine: WineAttributesSchema,
    whisky: WhiskyAttributesSchema,
    tradingCard: TradingCardAttributesSchema,
};
